import React, { useEffect, useState, useRef } from "react";
import { withCookies } from "react-cookie";
import SimpleCrypto from "simple-crypto-js";
import ApiManager from "./apiManager";
import config from "./baseConfig";
import Modal from "./components/Fragments/Modal";
import { localStorageSetItem, localStorageGetItem } from "./storage";

const LAST_ACTIVITY_KEY = "lastUserActivity";
const LOGOUT_ACTION_KEY = "logoutAction";
const ACTIVITY_PING_INTERVAL = 5000;

function IdleLogout(props) {
  const { cookies, history, csrf_token, setTokenAction, trans: i18n } = props;
  const loginToken = cookies.get("loginToken");
  const getDecryptedToken = () =>
    new SimpleCrypto(config.simpleCryptoSecretKey).decrypt(loginToken);

  const [csrfToken, setCsrfToken] = useState(csrf_token?.data || null);
  const [showError, setShowError] = useState(false);
  const mounted = useRef(true);
  const pingIntervalRef = useRef(null);
  const lastCheckedActivityRef = useRef(0);

  const refreshCsrfAndLogout = () => {
    ApiManager.getRequest({}, "/csrf-token").then(
      (res) => {
        const fresh = res.data.data;
        setCsrfToken(fresh);
        setTokenAction(res.data);
        performLogout(fresh);
      },
      () => setShowError(true)
    );
  };

  const performLogout = (csrf = csrfToken) => {
    let jwt;
    try {
      jwt = getDecryptedToken();
    } catch (e) {
      console.error("JWT decrypt failed", e);
      setShowError(true);
      return;
    }

    if (!csrf || !jwt) {
      console.error("Missing CSRF or JWT");
      setShowError(true);
      return;
    }

    ApiManager.signOut({ _token: csrf, jwt }).then(
      (res) => {
        if (!mounted.current) return;
        switch (res.data.status) {
          case "OK":
            cookies.remove("loginToken", { path: config.urlPrefix });
            cookies.remove("refreshToken", { path: config.urlPrefix });
            localStorageSetItem("activeAccount", "");
            localStorageSetItem(LOGOUT_ACTION_KEY, Date.now().toString());
            history.push(config.urlPrefix);
            break;
          case "CSRF_TOKEN_MISMATCH":
            refreshCsrfAndLogout();
            break;
          case "UNAUTHORIZED":
            history.push(config.urlPrefix);
            break;
          default:
            setShowError(true);
        }
      },
      () => mounted.current && setShowError(true)
    );
  };

  const handleStorageChange = (e) => {
    if (!mounted.current || !loginToken) return;

    if (e.key === LAST_ACTIVITY_KEY) {
      const newActivityTime = parseInt(
        localStorageGetItem(LAST_ACTIVITY_KEY),
        10
      );
      if (newActivityTime > lastCheckedActivityRef.current) {
        lastCheckedActivityRef.current = newActivityTime;
        resetIdleTimerRef.current();
      }
    } else if (e.key === LOGOUT_ACTION_KEY) {
      window.location.reload();
    }
  };

  const resetIdleTimerRef = useRef(() => {});

  useEffect(() => {
    mounted.current = true;
    if (!loginToken) return;

    const LOGOUT_MS = config.logoutTimer;
    let outId;

    resetIdleTimerRef.current = () => {
      clearTimeout(outId);
      outId = setTimeout(() => performLogout(), LOGOUT_MS);
    };

    const reset = () => {
      const now = Date.now();
      localStorageSetItem(LAST_ACTIVITY_KEY, now.toString());
      lastCheckedActivityRef.current = now;
      resetIdleTimerRef.current();
    };

    window.addEventListener("storage", handleStorageChange);

    [
      "mousemove",
      "mousedown",
      "click",
      "touchstart",
      "touchmove",
      "keydown",
      "scroll",
    ].forEach((e) => window.addEventListener(e, reset));

    reset();

    pingIntervalRef.current = setInterval(() => {
      const lastActivity = localStorageGetItem(LAST_ACTIVITY_KEY);
      if (lastActivity) {
        const lastActivityTime = parseInt(lastActivity, 10);

        if (lastActivityTime > lastCheckedActivityRef.current) {
          lastCheckedActivityRef.current = lastActivityTime;
          resetIdleTimerRef.current();
        }
      }
    }, ACTIVITY_PING_INTERVAL);

    return () => {
      mounted.current = false;
      clearTimeout(outId);
      clearInterval(pingIntervalRef.current);
      window.removeEventListener("storage", handleStorageChange);
      [
        "mousemove",
        "mousedown",
        "click",
        "touchstart",
        "touchmove",
        "keydown",
        "scroll",
      ].forEach((e) => window.removeEventListener(e, reset));
    };
  }, [loginToken]);

  if (showError) {
    return (
      <Modal
        title="Error"
        description="Could not log you out automatically."
        agreeText="OK"
        agreeAction={() => setShowError(false)}
        showCancelButton={false}
      />
    );
  }
  return null;
}

export default withCookies(IdleLogout);
